/** Standalone persistence functions for V4 skill progression: export, import, and V3 migration. */
import { ConfigLoader } from '../systems/ConfigLoader';
import type { V4TreeProgress } from '../utils/Types';

/** Legacy tree key renames from V3 to V4. */
export const TREE_RENAMES: Record<string, string> = { combat: 'grit', stealth: 'specter' };

/** Serialize tree progress into a plain record suitable for JSON persistence. */
export function exportTreeProgress(treeProgress: Map<string, V4TreeProgress>): Record<string, V4TreeProgress> {
  const result: Record<string, V4TreeProgress> = {};
  for (const [name, prog] of treeProgress) {
    result[name] = {
      xp: prog.xp,
      uses: prog.uses,
      forkChoices: { ...prog.forkChoices },
    };
  }
  return result;
}

/**
 * Restore tree progress from a saved record, detecting V3 format and migrating if needed.
 * Rebuilds the firedUnlocks set so callers can reconcile which nodes are already unlocked.
 * Returns the set of node/fork IDs that should be marked as fired-unlocks.
 */
export function importTreeProgress(
  saved: Record<string, unknown>,
  treeProgress: Map<string, V4TreeProgress>,
): Set<string> {
  // Detect V3 format: has chosenNodes array instead of forkChoices
  const isV3 = Object.values(saved).some(v =>
    typeof v === 'object' && v !== null && 'chosenNodes' in (v as Record<string, unknown>),
  );

  if (isV3) {
    migrateFromV3(
      saved as Record<string, { xp?: number; uses?: number; chosenNodes?: string[] }>,
      treeProgress,
    );
  } else {
    // V4 format
    for (const [treeName, data] of Object.entries(saved)) {
      const existing = treeProgress.get(treeName);
      if (existing && typeof data === 'object' && data !== null) {
        const d = data as Partial<V4TreeProgress>;
        existing.xp = d.xp ?? 0;
        existing.uses = d.uses ?? 0;
        existing.forkChoices = d.forkChoices ? { ...d.forkChoices } : {};
      }
    }
  }

  // Rebuild unlocks from imported state
  const firedUnlocks = new Set<string>();
  for (const treeName of Object.keys(ConfigLoader.v4Trees)) {
    // Mark all reachable nodes with sufficient XP as unlocked
    const tree = treeProgress.get(treeName);
    if (!tree) continue;
    const allNodes = ConfigLoader.getV4AllNodes(treeName);
    for (const node of allNodes) {
      if (node.xpThreshold <= tree.xp && ConfigLoader.isV4NodeReachable(treeName, node.id, tree.forkChoices)) {
        // For fork options, only mark if chosen
        const forkInfo = ConfigLoader.getV4ForkForNode(node.id);
        if (forkInfo && forkInfo.treeName === treeName) {
          const fork = forkInfo.fork;
          if (tree.forkChoices[fork.forkId] === node.id) {
            firedUnlocks.add(node.id);
          }
        } else {
          firedUnlocks.add(node.id);
        }
      }
    }
  }

  return firedUnlocks;
}

/** Migrate V3 save data to V4 format. Preserves XP, drops node choices (player must re-fork). */
export function migrateFromV3(
  saved: Record<string, { xp?: number; uses?: number; chosenNodes?: string[] }>,
  treeProgress: Map<string, V4TreeProgress>,
): void {
  for (const [key, data] of Object.entries(saved)) {
    const treeName = TREE_RENAMES[key] ?? key;
    const existing = treeProgress.get(treeName);
    if (existing) {
      existing.xp = data.xp ?? 0;
      existing.uses = data.uses ?? 0;
      // V3 chosenNodes can't be directly mapped to V4 forkChoices
      // Player keeps XP but must re-choose forks
      existing.forkChoices = {};
    }
  }
}
